/*  CounsellorCallbackCard — homepage lead-capture card.
    ────────────────────────────────────────────────────────────────
    iter100.121 — Fully DB-driven. Every piece of copy, the featured
    counsellor's photo/name/credentials, the SLA numbers, and the
    live social-proof metrics come from `/api/landing/card`. No
    hardcoded strings, no hardcoded people.

    Admin edits:
      • Content     → /admin/landing-page → Content tab
      • Counsellors → /admin/landing-page → Counsellors tab
      • SLA/Hours   → /admin/landing-page → Settings tab

    Card falls back to a graceful offline state when no counsellor is
    `is_available_now`. Form submission still works so we don't drop
    an interested caller.
*/
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import { toast } from "sonner";
import {
    Phone, CheckCircle, ShieldCheck, ArrowRight, ChatCircleDots, User,
    Star, Clock, Headset, MapPin, Sparkle,
} from "@phosphor-icons/react";


// Decorative social-proof avatars — pure visual filler, not real patients.
const SOCIAL_AVATARS = [
    "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=facearea&facepad=2.5&w=80&h=80&q=80",
    "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?auto=format&fit=facearea&facepad=2.5&w=80&h=80&q=80",
    "https://images.unsplash.com/photo-1663575126966-781a47448d42?auto=format&fit=facearea&facepad=2.5&w=80&h=80&q=80",
    "https://images.pexels.com/photos/7581111/pexels-photo-7581111.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop",
];

// Concern chips mirror the Smile Quiz schema so PES reads both lead sources
// consistently. Kept in-component so the CMS admin doesn't accidentally
// break lead classification.
const CONCERN_CHIPS = [
    { id: "crowding", label: "Crowding" },
    { id: "spacing",  label: "Spacing / Gaps" },
    { id: "overbite", label: "Bite issues" },
    { id: "rotated",  label: "Crooked teeth" },
    { id: "general",  label: "Just curious" },
];


/*  Tiny mustache-style interpolator. Replaces {token} with values[token]
    or leaves the token in-place if we don't have a value (so it degrades
    gracefully during admin authoring). */
function interpolate(str, values) {
    if (!str) return "";
    return String(str).replace(/\{(\w+)\}/g, (m, k) => (values && values[k] != null ? String(values[k]) : m));
}


export default function CounsellorCallbackCard() {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState("form");    // form → enrich → done
    const [leadId, setLeadId] = useState(null);
    const [city, setCity] = useState("");
    const [concern, setConcern] = useState(null);
    const [enriching, setEnriching] = useState(false);

    // DB-driven payload — every string, counsellor, and metric.
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/landing/card")
            .then((r) => { if (!cancelled) setPayload(r.data || {}); })
            .catch(() => { if (!cancelled) setPayload({}); });   // degrade to empty so component still renders
        return () => { cancelled = true; };
    }, []);

    const cms = payload?.cms || {};
    const settings = payload?.settings || {};
    const metrics = payload?.metrics || {};
    const counsellor = payload?.featured_counsellor || null;

    const sla = settings.callback_sla_minutes ?? 8;
    const callDuration = settings.call_duration_minutes ?? 15;
    const weekly = metrics.weekly_callbacks_count ?? 0;
    const trendingLabel = metrics?.trending_concern?.label || null;

    const counsellorFirst = counsellor?.name?.split(" ")?.[0] || "our specialist";
    const callerFirst = name.trim().split(/\s+/)[0] || "there";

    /* Read a CMS string by short key (headline, subheadline, etc.), or fall
       back to a fallback value. Handles both short and prefixed forms. */
    const t = (key, fallback = "") => (cms[key] ?? cms[`counsellor_card.${key}`] ?? fallback);

    // Interpolation context — every {token} we support in the CMS values.
    const iCtx = {
        callback_sla_minutes: sla,
        reply_time_minutes: settings.reply_time_minutes ?? sla,
        call_duration_minutes: callDuration,
        counsellor_name: counsellor?.name || "Our specialist",
        counsellor_first_name: counsellorFirst,
        caller_first_name: callerFirst,
        weekly_callbacks_count: weekly,
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (name.trim().length < 2) { toast.error("Please enter your name."); return; }
        if (!isValid10Digit(phone)) { toast.error("Enter your 10-digit mobile number."); return; }
        if (!consent) { toast.error("Please agree to be contacted before continuing."); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/counsellor-callback", {
                name,
                phone: toE164(phone),
                notes: notes.trim(),
                consent_given: true,
            });
            setLeadId(r.data?.id || null);
            setStep("enrich");
            toast.success(`Thanks ${callerFirst}! One more quick step (optional).`);
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const doneToast = () => toast.success(
        interpolate(t("done_subtitle", "{counsellor_name} will call you within {callback_sla_minutes} minutes."), iCtx)
    );

    const skipEnrich = () => { setStep("done"); doneToast(); };

    const submitEnrich = async () => {
        if (!city.trim() && !concern) return skipEnrich();
        if (!leadId) return skipEnrich();
        setEnriching(true);
        try {
            await api.post("/counsellor-callback/enrich", {
                lead_id: leadId,
                city: city.trim() || "",
                primary_concern: concern || null,
            });
            setStep("done");
            doneToast();
        } catch {
            setStep("done");
            doneToast();
        } finally { setEnriching(false); }
    };

    // ─── STEP 2 (optional enrichment) ─────────────────────────────
    if (step === "enrich") {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-[#003E50] to-[#001a23] p-8 sm:p-12 bento-shadow"
                data-testid="counsellor-card-enrich"
            >
                <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-2xl mx-auto">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="h-1.5 flex-1 rounded-full bg-emerald-400/60" />
                        <div className="h-1.5 flex-1 rounded-full bg-amber-300/60" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/60 pl-2">Step 2 / 2 · Optional</span>
                    </div>

                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-amber-300/20 border border-amber-200/30 grid place-items-center shrink-0">
                            <Sparkle size={22} weight="duotone" className="text-amber-200" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight" data-testid="counsellor-enrich-title">
                                {interpolate(t("enrich_title", "Help {counsellor_first_name} prepare?"), iCtx)}
                            </h3>
                            <p className="text-white/75 text-sm mt-1.5 leading-relaxed" data-testid="counsellor-enrich-subtitle">
                                {interpolate(t("enrich_subtitle", "A 10-second tap so they walk in with context, not cold. Totally optional — your callback is already booked."), iCtx)}
                            </p>
                        </div>
                    </div>

                    <div className="mt-7">
                        <label htmlFor="enrich-city" className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70 mb-2 inline-flex items-center gap-1.5">
                            <MapPin size={12} weight="fill" /> Your city
                        </label>
                        <Input
                            id="enrich-city"
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder="e.g. Mumbai, Bengaluru, Delhi…"
                            className="h-12 rounded-2xl bg-white/95 border-transparent text-sm placeholder:text-muted-foreground/70"
                            data-testid="counsellor-enrich-city"
                        />
                        <p className="text-[11px] text-white/55 mt-1.5">We&apos;ll suggest a partner clinic near you.</p>
                    </div>

                    <div className="mt-6">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70 mb-2 inline-flex items-center gap-1.5">
                            <ChatCircleDots size={12} weight="fill" /> What&apos;s your main concern?
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="counsellor-enrich-concerns">
                            {CONCERN_CHIPS.map((c) => {
                                const active = concern === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setConcern(active ? null : c.id)}
                                        className={`px-3.5 h-9 text-[12.5px] font-semibold rounded-full transition-all ${
                                            active
                                                ? "bg-amber-300 text-primary shadow-md ring-2 ring-amber-200/40"
                                                : "bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-white/15"
                                        }`}
                                        data-testid={`counsellor-enrich-concern-${c.id}`}
                                        aria-pressed={active}
                                    >
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Button
                            type="button"
                            onClick={submitEnrich}
                            disabled={enriching}
                            className="flex-1 h-13 rounded-2xl bg-gradient-to-r from-amber-300 to-amber-400 hover:from-amber-200 hover:to-amber-300 text-primary font-bold tracking-wide shadow-xl text-[14.5px] py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="counsellor-enrich-submit"
                        >
                            {enriching ? "Saving…" : (
                                <>
                                    {(city.trim() || concern) ? `Send to ${counsellorFirst}` : "All done"}
                                    <ArrowRight size={16} weight="bold" className="ml-2" />
                                </>
                            )}
                        </Button>
                        <Button
                            type="button"
                            onClick={skipEnrich}
                            disabled={enriching}
                            variant="ghost"
                            className="h-13 rounded-2xl text-white/85 hover:bg-white/10 hover:text-white font-semibold text-[13.5px] py-3 px-5"
                            data-testid="counsellor-enrich-skip"
                        >
                            Skip — call me anyway
                        </Button>
                    </div>
                </div>
            </motion.div>
        );
    }

    // ─── STEP 3 (confirmation) ───────────────────────────────────
    if (step === "done") {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-[#003E50] to-[#001a23] p-10 sm:p-14 bento-shadow text-center"
                data-testid="counsellor-card-done"
            >
                <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="relative">
                    <div className="w-20 h-20 mx-auto rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-6 shadow-lg">
                        <CheckCircle size={42} weight="fill" className="text-amber-300" />
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="counsellor-done-title">
                        {interpolate(t("done_title", "We've got you, {caller_first_name}."), iCtx)}
                    </h3>
                    <p className="text-white/80 mt-4 max-w-lg mx-auto text-base" data-testid="counsellor-done-subtitle">
                        {interpolate(t("done_subtitle", "{counsellor_name} will call you within {callback_sla_minutes} minutes to plan your smile journey. No commitment, just clarity."), iCtx)}
                    </p>
                </div>
            </motion.div>
        );
    }

    // ─── STEP 1 (form) ───────────────────────────────────────────
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-[#003E50] to-[#001a23] p-7 sm:p-10 lg:p-12 bento-shadow"
            data-testid="counsellor-card"
        >
            <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-primary/40 blur-3xl pointer-events-none opacity-60" aria-hidden="true" />

            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }}
                aria-hidden="true"
            />

            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                {/* LEFT: counsellor introduction */}
                <div className="lg:col-span-5 text-white">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white text-[10px] font-bold tracking-[0.18em] uppercase mb-5" data-testid="counsellor-pill">
                        <span className="relative flex h-2 w-2">
                            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${counsellor?.is_available_now ? "bg-green-400 animate-ping" : "bg-slate-400"}`} />
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${counsellor?.is_available_now ? "bg-green-400" : "bg-slate-400"}`} />
                        </span>
                        <ChatCircleDots size={11} weight="fill" /> {t("pill_text", "Free Expert Smile Consultation")}
                    </div>

                    <h3 className="font-display text-2xl sm:text-[1.75rem] font-semibold tracking-tight leading-snug" data-testid="counsellor-headline">
                        {t("headline", "Not sure if aligners are right for you?")}
                    </h3>
                    <p className="mt-3 text-white/80 text-[15px] leading-relaxed" data-testid="counsellor-subheadline">
                        {interpolate(t("subheadline", "Get a free, no-obligation answer from a certified smile expert — in under {call_duration_minutes} minutes."), iCtx)}
                    </p>

                    {counsellor ? (
                        /* Hero specialist card — dynamic counsellor */
                        <div className="mt-6 flex items-center gap-5 p-5 bg-white/10 backdrop-blur-md rounded-3xl border border-white/15 shadow-xl" data-testid="counsellor-hero-card">
                            <div className="relative shrink-0">
                                {counsellor.photo_url ? (
                                    <img
                                        src={counsellor.photo_url}
                                        alt={`${counsellor.name}, ${counsellor.designation}`}
                                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover shadow-2xl ring-2 ring-amber-300/60"
                                        loading="lazy"
                                        data-testid="counsellor-hero-photo"
                                    />
                                ) : (
                                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 grid place-items-center text-primary font-display font-bold text-3xl shadow-2xl ring-2 ring-amber-300/60">
                                        {counsellor.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}
                                    </div>
                                )}
                                {counsellor.is_available_now && (
                                    <span className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-green-400 ring-[3px] ring-primary">
                                        <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" />
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-lg sm:text-xl leading-tight flex items-center gap-2 flex-wrap" data-testid="counsellor-hero-name">
                                    {counsellor.name}{counsellor.credentials ? `, ${counsellor.credentials}` : ""}
                                    {counsellor.rating > 0 && (
                                        <span className="inline-flex items-center gap-0.5 text-amber-300" data-testid="counsellor-hero-rating">
                                            <Star size={13} weight="fill" />
                                            <span className="text-xs font-bold">{counsellor.rating.toFixed(1)}</span>
                                        </span>
                                    )}
                                </div>
                                {counsellor.designation && (
                                    <div className="text-[12px] text-white/80 mt-1" data-testid="counsellor-hero-designation">{counsellor.designation}</div>
                                )}
                                {counsellor.ratings_count > 0 && (
                                    <div className="text-[11px] text-white/70 mt-0.5">Rated by {counsellor.ratings_count}+ patients</div>
                                )}
                                {counsellor.is_available_now && (
                                    <div className="text-[11px] text-green-400 font-semibold mt-1.5 flex items-center gap-1.5">
                                        <span className="relative flex h-1.5 w-1.5">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
                                        </span>
                                        Online now · Replies in &lt; {settings.reply_time_minutes ?? sla} min
                                    </div>
                                )}
                                {counsellor.languages?.length > 0 && (
                                    <div className="text-[10px] text-white/55 mt-2 tracking-wide" data-testid="counsellor-hero-languages">
                                        Speaks {counsellor.languages.join(" · ")}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Offline fallback — no counsellor available_now. Form still works. */
                        <div className="mt-6 p-5 bg-amber-300/10 backdrop-blur-md rounded-3xl border border-amber-200/25 shadow-xl flex items-start gap-4" data-testid="counsellor-offline-card">
                            <div className="w-12 h-12 rounded-2xl bg-amber-300/20 border border-amber-200/30 grid place-items-center shrink-0">
                                <Clock size={22} weight="duotone" className="text-amber-200" />
                            </div>
                            <p className="text-[13.5px] text-white/85 leading-relaxed" data-testid="counsellor-offline-message">
                                {interpolate(t("offline_message", "Our specialists are currently offline. Leave your number and we'll call you back within {callback_sla_minutes} minutes."), iCtx)}
                            </p>
                        </div>
                    )}

                    {/* Social proof strip */}
                    {weekly > 0 && (
                        <div className="mt-5 flex items-center gap-3.5 p-3.5 rounded-2xl bg-white/8 backdrop-blur-md border border-white/12" data-testid="counsellor-social-proof">
                            <div className="flex -space-x-2.5">
                                {SOCIAL_AVATARS.map((src, i) => (
                                    <img
                                        key={src}
                                        src={src}
                                        alt=""
                                        aria-hidden="true"
                                        className="w-10 h-10 rounded-full ring-2 ring-primary object-cover bg-white/10 shadow-md"
                                        style={{ zIndex: SOCIAL_AVATARS.length - i }}
                                        loading="lazy"
                                    />
                                ))}
                            </div>
                            <div className="leading-tight">
                                <div className="font-display text-lg font-bold text-white">
                                    <span data-testid="counsellor-weekly-count">{weekly}+</span> <span className="text-amber-300">callbacks</span>
                                </div>
                                <div className="text-[11px] text-white/70">{t("social_proof_suffix", "scheduled this week")}</div>
                            </div>
                        </div>
                    )}

                    {/* Trending concern chip */}
                    {trendingLabel && (
                        <div
                            className="mt-3 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-amber-300/15 backdrop-blur-md border border-amber-200/30"
                            data-testid="counsellor-top-concern"
                        >
                            <Sparkle size={13} weight="fill" className="text-amber-300 shrink-0" />
                            <span className="text-[11.5px] text-white/85 leading-tight">
                                {t("trending_prefix", "Most common ask this month:")} <span className="font-bold text-amber-200">{trendingLabel}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* RIGHT: form on a light card */}
                <form
                    onSubmit={onSubmit}
                    className="lg:col-span-7 relative p-6 sm:p-7 rounded-3xl bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl"
                    data-testid="counsellor-form"
                >
                    <div className="flex items-center gap-2.5 mb-5">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center shadow-md">
                            <Headset size={18} weight="fill" color="white" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-base text-foreground leading-tight" data-testid="counsellor-form-title">{t("form_title", "Request a callback")}</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                <Clock size={11} weight="fill" className="text-primary" />
                                {t("form_hint", "Avg. callback time:")} <span className="font-semibold text-foreground">{sla} minutes</span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="relative">
                            <User size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t("name_placeholder", "Your name")}
                                className="h-13 pl-12 rounded-2xl border-border bg-white text-sm py-3"
                                data-testid="counsellor-name-input"
                            />
                        </div>
                        <div className="relative">
                            <PhoneInput10
                                value={phone}
                                onChange={setPhone}
                                inputClassName="h-13 rounded-r-2xl bg-white text-sm py-3"
                                className="h-13"
                                placeholder={t("phone_placeholder", "Your mobile — we'll call you back")}
                                testid="counsellor-phone-input"
                            />
                        </div>
                        <div className="relative">
                            <ChatCircleDots size={18} weight="duotone" className="absolute left-4 top-4 text-muted-foreground pointer-events-none" />
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t("notes_placeholder", "E.g. I have crowding, gaps, or just want to know what it'll cost…")}
                                rows={2}
                                maxLength={500}
                                className="w-full rounded-2xl border border-border bg-white text-sm pl-12 pr-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                                data-testid="counsellor-notes-input"
                            />
                        </div>

                        <label
                            htmlFor="counsellor-consent"
                            className="flex items-start gap-2.5 cursor-pointer select-none pt-1"
                            data-testid="counsellor-consent-label"
                        >
                            <Checkbox
                                id="counsellor-consent"
                                checked={consent}
                                onCheckedChange={(v) => setConsent(Boolean(v))}
                                className="mt-0.5 shrink-0 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                data-testid="counsellor-consent-checkbox"
                            />
                            <span className="text-[12px] leading-snug text-muted-foreground" data-testid="counsellor-consent-copy">
                                {t("consent_copy", "I agree to be contacted about my query. No spam, ever — opt out anytime.")}
                            </span>
                        </label>

                        <div className="relative group pt-1">
                            <span
                                className={`absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-400/30 via-primary/30 to-cyan-400/30 blur-lg pointer-events-none transition-opacity ${
                                    consent ? "animate-pulse opacity-100" : "opacity-30"
                                }`}
                                aria-hidden="true"
                            />
                            <Button
                                type="submit"
                                size="lg"
                                disabled={submitting || !consent}
                                className="relative w-full h-14 rounded-2xl bg-gradient-to-r from-primary via-[#005a73] to-[#007C9D] hover:from-[#003E50] hover:via-primary hover:to-[#005a73] text-white font-bold tracking-wide shadow-xl text-[15px] transition-all group-hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="counsellor-submit-btn"
                            >
                                {submitting ? (
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.15s" }} />
                                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.3s" }} />
                                    </span>
                                ) : (
                                    <>
                                        {t("cta_text", "Book My Free 15-Min Call")}
                                        <ArrowRight size={18} weight="bold" className="ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Trust footer — single interpolated CMS string, no more hardcoded triple-badge */}
                    <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-center gap-2 text-[11.5px] text-muted-foreground text-center leading-snug" data-testid="counsellor-trust-footer">
                        <ShieldCheck size={13} weight="fill" className="text-primary shrink-0" />
                        {interpolate(t("trust_footer", "Free & confidential · No clinic visit needed · We call you within {callback_sla_minutes} minutes"), iCtx)}
                    </div>
                </form>
            </div>
        </motion.div>
    );
}
